import { DomainError } from '@glory/kernel';

/**
 * «Для клиента» в проекте (кабинет клиента, часть 10.8): что студия даёт клиенту и показывает ему.
 * - `task` — задание: собрать материалы, прислать доступы; клиент отвечает текстом и файлами, студия принимает или
 *   возвращает с комментарием;
 * - `approval` — на согласование: макет, текст, решение; клиент согласует или пишет замечания, студия присылает новую
 *   версию;
 * - `progress` — прогресс: скриншоты и пояснение, ответа не ждёт.
 */
export const CLIENT_ITEM_KINDS = ['task', 'approval', 'progress'] as const;
export type ClientItemKind = (typeof CLIENT_ITEM_KINDS)[number];

/**
 * Состояние: задание — `open` (ждёт клиента) → `submitted` (клиент ответил) → `done` (принято) или снова `open`
 * (вернули); согласование — `pending` (ждёт клиента) → `approved` или `changes` (замечания) → `pending` (новая
 * версия); прогресс — `published`.
 */
export const CLIENT_ITEM_STATUSES = [
  'open',
  'submitted',
  'done',
  'pending',
  'approved',
  'changes',
  'published',
] as const;
export type ClientItemStatus = (typeof CLIENT_ITEM_STATUSES)[number];

/** Что делает клиент: ответить на задание, согласовать, прислать замечания. */
export const CLIENT_ACTIONS = ['answer', 'approve', 'changes'] as const;
export type ClientAction = (typeof CLIENT_ACTIONS)[number];

/** Что делает студия: принять ответ, вернуть задание, прислать новую версию на согласование. */
export const STUDIO_ACTIONS = ['accept', 'return', 'resend'] as const;
export type StudioAction = (typeof STUDIO_ACTIONS)[number];

export const CLIENT_ITEM_TITLE_MAX = 200;
export const CLIENT_ITEM_TEXT_MAX = 10_000;
export const CLIENT_REPLY_TEXT_MAX = 4_000;
/** Файлов в одном пункте или ответе. */
export const CLIENT_ITEM_FILES_MAX = 20;

export const initialStatus = (kind: ClientItemKind): ClientItemStatus =>
  kind === 'task' ? 'open' : kind === 'approval' ? 'pending' : 'published';

/** Ждёт клиента: попадает в «Нужно от вас». */
export const awaitsClient = (status: ClientItemStatus): boolean => status === 'open' || status === 'pending';

/** Ждёт студию: клиент ответил или прислал замечания. */
export const awaitsStudio = (status: ClientItemStatus): boolean =>
  status === 'submitted' || status === 'changes';

/** Закрыт: принят или согласован. */
export const isResolved = (status: ClientItemStatus): boolean => status === 'done' || status === 'approved';

const CLIENT_MOVES: Readonly<
  Record<
    ClientAction,
    { readonly kind: ClientItemKind; readonly from: ClientItemStatus; readonly to: ClientItemStatus }
  >
> = {
  answer: { kind: 'task', from: 'open', to: 'submitted' },
  approve: { kind: 'approval', from: 'pending', to: 'approved' },
  changes: { kind: 'approval', from: 'pending', to: 'changes' },
};

const STUDIO_MOVES: Readonly<
  Record<
    StudioAction,
    {
      readonly kind: ClientItemKind;
      readonly from: readonly ClientItemStatus[];
      readonly to: ClientItemStatus;
    }
  >
> = {
  accept: { kind: 'task', from: ['submitted'], to: 'done' },
  return: { kind: 'task', from: ['submitted', 'done'], to: 'open' },
  resend: { kind: 'approval', from: ['changes', 'approved'], to: 'pending' },
};

/** Ответ клиента: допустим только из состояния, которое ждёт клиента; иначе — «уже ответили». */
export function clientMove(
  kind: ClientItemKind,
  status: ClientItemStatus,
  action: ClientAction,
): ClientItemStatus {
  const move = CLIENT_MOVES[action];
  if (move.kind !== kind) throw new DomainError('projects.client_item_action');
  if (move.from !== status) throw new DomainError('projects.client_item_answered');
  return move.to;
}

/** Действие студии: принять и вернуть — у задания, новая версия — у согласования. */
export function studioMove(
  kind: ClientItemKind,
  status: ClientItemStatus,
  action: StudioAction,
): ClientItemStatus {
  const move = STUDIO_MOVES[action];
  if (move.kind !== kind) throw new DomainError('projects.client_item_action');
  if (!move.from.includes(status)) throw new DomainError('projects.client_item_state');
  return move.to;
}

/** Замечания и возврат без комментария непонятны: что поправить. */
export function needsText(action: ClientAction | StudioAction): boolean {
  return action === 'changes' || action === 'return';
}
